import { useFeatureFlagStore } from '@/stores/features'
import CompanySuggestionController from '@controllers/Investors/CompanySuggestionController'
import axios from 'axios'
import { useToast } from 'primevue/usetoast'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

type CompanySuggestionData = App.Data.Company.CompanySuggestionData

const MIN_QUERY_LENGTH = 2

export function useCompanySuggestions() {
  const features = useFeatureFlagStore()
  const toast = useToast()
  const { t } = useI18n()

  const suggestions = ref<CompanySuggestionData[]>([])
  const loading = ref(false)
  const latestQuery = ref<string | null>(null)

  const enabled = computed(() => features.enabled('handelsregister_ai'))

  function reportFailure(error: unknown): void {
    if (!axios.isAxiosError<{ key?: string }>(error) || !error.response?.data?.key) {
      return
    }

    const key = error.response.data.key

    toast.add({
      severity: 'error',
      summary: t(`toasts.${key}.summary`),
      detail: t(`toasts.${key}.detail`),
      life: 5000,
    })

    if (key === 'handelsregister.limit.global_reached') {
      features.loadFeatures()
    }
  }

  async function search(query: string): Promise<void> {
    const trimmed = query.trim()
    latestQuery.value = trimmed

    if (trimmed.length < MIN_QUERY_LENGTH) {
      suggestions.value = []

      return
    }

    loading.value = true

    try {
      const response = await axios.get<CompanySuggestionData[]>(CompanySuggestionController().url, {
        params: { query: trimmed },
      })

      if (latestQuery.value === trimmed) {
        suggestions.value = response.data
      }
    } catch (error) {
      if (latestQuery.value === trimmed) {
        suggestions.value = []
      }

      reportFailure(error)
    } finally {
      if (latestQuery.value === trimmed) {
        loading.value = false
      }
    }
  }

  function reset(): void {
    suggestions.value = []
    latestQuery.value = null
  }

  return { enabled, suggestions, loading, search, reset }
}
